import { z } from 'zod';

import { validateJSONString } from '@nw/shared/utils';

const PeriodicInstallmentDetailsSchema = z
  .object({
    config_id: z.string()
  })
  .optional();

export const GetProductDetailsRequestSchema = z.object({
  invite_code: z.string().optional(),
  affiliate_code: z.string().optional(),
  periodic_installment_details: PeriodicInstallmentDetailsSchema
});

export type GetProductDetailsRequestType = z.infer<
  typeof GetProductDetailsRequestSchema
>;

const ErrorMessageObjectSchema = z.object({
  alreadyPaidForPartialProduct: z.string().optional(),
  purchaseEligibilityDidNotMatch: z.string().optional(),
  alreadyRegistered: z
    .union([
      z.string(),
      z.object({
        action: z.string()
      })
    ])
    .optional(),
  productExpired: z.string().optional()
});

const InstallmentsDetailsObjectSchema = z.object({
  pathId: z.string(),
  installmentId: z.string(),
  noOfInstallments: z.string()
});

const RegistrationFormDetailsSchema = z.object({
  form_info_heading: z.string().optional(),
  form_info_secondary_mobile_heading: z.string().optional(),
  form_info_description: z.string().optional(),
  speakers_image_urls: z
    .object({
      desktop: z.record(z.string(), z.string()),
      mobile: z.record(z.string(), z.string())
    })
    .optional(),
  speakers_bg_image_urls: z
    .object({
      desktop: z.string().optional(),
      mobile: z.string().optional()
    })
    .optional(),
  should_show_mobile_heading: z.boolean().optional(),
  webinar_date_details: z
    .object({
      webinar_dates: z.string()
    })
    .optional()
});

const CollegesWebinarSpeakerDetailsSchema = z.object({
  speaker_name: z.string(),
  speaker_designation: z.string(),
  speaker_description: z.string(),
  speaker_image_url: z.string()
});

const CollegesWebinarRegistrationFormDetailsSchema = z.object({
  webinar_title: z.string(),
  title_description: z.string(),
  webinar_heading: z.string(),
  webinar_secondary_heading: z.string(),
  heading_description: z.string(),
  speaker_details: CollegesWebinarSpeakerDetailsSchema.optional()
});

const ProductExtraDataSchema = z.object({
  icons: z.object({
    icon1: z.string(),
    alt_text: z.string().optional()
  }),
  order: z.number(),
  product_name: z.string(),
  form_type: z.string().optional(),
  page_description: z.string().optional(),
  card_header: z.string().optional(),
  card_profile_header_price: z.string().optional(),
  should_show_refund_policy: z.boolean().optional(),
  refund_policy_content: z.string().optional(),
  for_more_details: z.string().optional(),
  payment_details_heading: z.string().optional(),
  payment_details_heading_description: z.string().optional(),
  payment_error_messages: ErrorMessageObjectSchema.optional(),
  should_show_program_info: z.boolean().optional(),
  should_show_payment_terms_and_conditions: z.boolean().optional(),
  should_show_coupon_apply_button: z.boolean().optional(),
  eligible_for_program_change: z.boolean().optional(),
  payment_details_submit_button_text: z.string().optional(),
  submit_button_id: z.string().optional(),
  should_show_omega_fields: z.boolean().optional(),
  periodic_installments_details: z
    .array(InstallmentsDetailsObjectSchema)
    .optional(),
  cta_text: z.string().optional(),
  registration_form_variant: z.string().optional(),
  registration_form_details: RegistrationFormDetailsSchema.optional(),
  colleges_webinar_registration_form_details:
    CollegesWebinarRegistrationFormDetailsSchema.optional(),
  should_format_request_object: z.boolean().optional()
});

const RequestObjectExtraFieldsDataSchema = z.array(
  z.object({
    field: z.string(),
    tags: z.array(
      z.enum(['NON_PII', 'BASIC_PII', 'INTERMEDIATE_PII', 'CRITICAL_PII'])
    ),
    field_name_in_request_object: z.string().optional()
  })
);

const StepTypeEnum = z.enum([
  'DEFAULT',
  'AUTHENTICATION_STEP',
  'EMBEDDED_SUCCESS_FORM',
  'EMBEDDED_SUCCESS_PAGE',
  'CUSTOM_SUCCESS_PAGE',
  'EMBEDDED_SUCCESS_PAGE_WITHOUT_SLOTS',
  'WEBINAR_CTA_SUCCESS_PAGE'
]);

const optionalFieldsDataStepTypes = [
  'EMBEDDED_SUCCESS_PAGE',
  'WEBINAR_CTA_SUCCESS_PAGE'
];

const NextStepDetailsSchema = z.object({
  step_id: z.string(),
  btn_text: z.string(),
  btn_id: z.string(),
  btn_actions: z.array(z.string()).optional()
});

const StepInfoSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  step_ui_type: z.string().optional(),
  should_show_limited_seat_cta: z.boolean().optional(),
  progress_bar_info: z
    .object({
      type: z.string(),
      total_steps: z.number(),
      current_step: z.number()
    })
    .optional(),
  extra_header_ui_details: z
    .object({
      type: z.string()
    })
    .optional(),
  cta_details: z
    .array(
      z.object({
        cta_text: z.string().optional(),
        cta_id: z.string().optional(),
        cta_type: z.string().optional(),
        cta_actions: z.array(z.string()).optional()
      })
    )
    .optional()
});

const ValidationSchema = z.object({
  type: z.string().optional(),
  params: z.array(z.union([z.string(), z.number()])).optional(),
  method: z.string().optional(),
  error_message: z.string().optional(),
  eval_prop: z.string().optional(),
  eval_value: z.number().optional()
});

const TimeLapsedOptionSchema = z.object({
  start_time: z.string().optional(),
  end_time: z.string().optional(),
  value: z.string(),
  max_visibility_time: z.string().optional(),
  max_visibility_time_in_days: z.number().optional(),
  preferred_days: z.array(z.number()).optional()
});

const SlotSelectionFieldTimeLapsedOptionSchema = z.object({
  time_value: z.string(),
  max_visibility_time: z.string(),
  preferred_days: z.array(z.number()),
  match_keys: z.array(z.string()).optional(),
  condition: z.string().optional(),
  option_type: z.string().optional(),
  lead_type: z.string().optional(),
  extra_dates_to_show_slot: z.array(z.string()).optional(),
  slot_hold_dates: z.array(z.string()).optional(),
  is_immediate_slot: z.boolean().optional()
});

const DayWiseSlotHoldDatesDetailSchema = z.object({
  date: z.string(),
  reason: z.string().optional(),
  condition: z.string().optional()
});

const DropdownOptionSchema = z.object({
  value: z.string(),
  label: z.string()
});

const DependentOnOptionConfigsSchema = z.object({
  dependent_on_field_name_enum: z.string(),
  dependent_on_options: z.array(
    z.object({
      dependent_on_field_value: z.string(),
      options: z.array(DropdownOptionSchema)
    })
  )
});

const FormFieldsSchema = z.object({
  isRelevant: z.boolean().optional(),
  isHidden: z.boolean().optional(),
  relevantCondition: z.string().optional(),
  type: z.string(),
  field: z.string(),
  label: z.string().optional(),
  options: z.union([
    z.array(DropdownOptionSchema),
    z.array(z.string()),
    z.undefined()
  ]),
  enable_default_suggestion: z.boolean().optional(),
  is_dependent: z.boolean().optional(),
  dependent_fields: z.array(z.string()).optional(),
  dependent_on_field: z.string().optional(),
  search_key: z.string().optional(),
  select_type: z.string().optional(),
  is_tnc: z.boolean().optional(),
  tnc_enum: z.string().optional(),
  validation_type: z.string().optional(),
  regex_type: z.string().optional(),
  validation_message: z.string().optional(),
  validations: z.array(ValidationSchema).optional(),
  field_data: z
    .object({
      terms_and_conditions_url: z.string().optional()
    })
    .optional(),
  helper_text: z.string().optional(),
  min_date: z.string().optional(),
  max_date: z.string().optional(),
  filter_date_expression: z.string().optional(),
  computed_string: z.string().optional(),
  should_show_otp_field: z.boolean().optional(),
  placeholder: z.string().optional(),
  should_hide_receive_updates: z.boolean().optional(),
  shouldShowLabel: z.boolean().optional(),
  labelHelperText: z.string().optional(),
  should_include_in_create_order: z.boolean().optional(),
  pattern: z.string().optional(),
  s3_upload_bucket_path: z.string().optional(),
  supporting_field: z.string().optional(),
  supporting_field_helper_text: z.string().optional(),
  supporting_placeholder: z.string().optional(),
  placeholder_helper_text: z.string().optional(),
  supporting_placeholder_helper_text: z.string().optional(),
  shouldPrefilledFromUserProfile: z.boolean().optional(),
  shouldCheckAdditionalRelevantCondition: z.boolean().optional(),
  is_expected_removal: z.boolean().optional(),
  isMarginBottomZero: z.boolean().optional(),
  should_show_icon: z.boolean().optional(),
  relevantLabel: z.string().optional(),
  options_type: z.string().optional(),
  time_lapsed_options: z.array(TimeLapsedOptionSchema).optional(),
  show_only_slot_time: z.boolean().optional(),
  should_show_only_label: z.boolean().optional(),
  default_value: z.boolean().optional(),
  mobile_placeholder: z.string().optional(),
  otpInputType: z.string().optional(),
  notificationChannels: z.array(z.string()).optional(),
  slot_options: z.array(SlotSelectionFieldTimeLapsedOptionSchema).optional(),
  intensive_slots_language_options: z.record(z.array(z.string())).optional(),
  academy_slots_language_options: z.record(z.array(z.string())).optional(),
  day_wise_slot_hold_dates: z
    .array(DayWiseSlotHoldDatesDetailSchema)
    .optional(),
  slotConditions: z.record(z.string()).optional(),
  slots_ui_type: z.string().optional(),
  is_date_based_slots: z.boolean().optional(),
  dependent_on_fields: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  conditionally_dependent_values: z
    .array(
      z.object({
        condition: z.string(),
        value: z.string(),
        isConditionalValue: z.boolean().optional()
      })
    )
    .optional(),
  get_otp_button_actions: z.array(z.string()).optional(),
  isCloseAllow: z.boolean().optional(),
  imgLink: z.string().optional(),
  alt_text: z.string().optional(),
  description: z.string().optional(),
  heading: z.string().optional(),
  minimum_select_field_allowed: z.number().optional(),
  initial_select_field_number: z.number().optional(),
  add_select_field_text: z.string().optional(),
  should_show_error_icon: z.boolean().optional(),
  name: z.string().optional(),
  placeholder_text: z.string().optional(),
  state_field_name: z.string().optional(),
  path_to_upload: z.string().optional(),
  acl_type: z.string().optional(),
  max_size_to_upload_in_mb: z.number().optional(),
  dependent_on_option_configs: DependentOnOptionConfigsSchema.optional(),
  should_verify_email: z.boolean().optional(),
  is_required: z.boolean().optional(),
  date_format: z.string().optional(),
  max_visibility_time: z.string().optional(),
  exclude_dates: z.array(z.string()).optional(),
  show_month_year_picker: z.boolean().optional(),
  custom_yup_validation: z.string().optional()
});

const StepConfigSchema = z
  .object({
    step_id: z.string(),
    step_type: StepTypeEnum,
    next_step_details: NextStepDetailsSchema.optional(),
    previous_step_details: NextStepDetailsSchema.optional(),
    step_info: StepInfoSchema.optional(),
    fields_data: z.array(FormFieldsSchema).optional()
  })
  .refine(
    (data) => {
      if (
        !optionalFieldsDataStepTypes.includes(data.step_type) &&
        data.fields_data === undefined
      ) {
        return false;
      }
      return true;
    },
    {
      message:
        'fields_data is required unless step_type allows it to be optional'
    }
  );

const MultiStepFormConfigSchema = z.object({
  initial_step_id: z.string(),
  step_details: z.array(StepConfigSchema)
});

const MultiStepFormFieldsDataSchema = z.object({
  registration_form_details: MultiStepFormConfigSchema,
  request_object_extra_fields_data: RequestObjectExtraFieldsDataSchema,
  form_type: z.enum(['MULTI_STEP_PRODUCT_REGISTRATION']),
  design_system_type: z.enum(['SHADCN_DESIGN_SYSTEM']).optional()
});

const SuccessPaymentMessageBasedOnFieldSchema = z.object({
  title: z.string(),
  description: z.string(),
  url: z.string(),
  success_button_text: z.string().optional(),
  show_success_button: z.boolean().optional(),
  condition: z.string()
});

const WhatsappButtonConfigSchema = z.object({
  text: z.string().optional(),
  phone: z.string().optional()
});

const FailurePaymentSchema = z.object({
  title: z.string(),
  description: z.string(),
  url: z.string().optional(),
  failure_button_text: z.string(),
  button_type: z.string().optional(),
  whatsapp_button_config: WhatsappButtonConfigSchema.optional()
});

const SuccessPaymentSchema = z.object({
  title: z.string(),
  description: z.string(),
  url: z.string(),
  success_button_text: z.string().optional(),
  show_success_button: z.boolean().optional()
});

const AslWebinarRedirectionUrlsSchema = z.object({
  Telugu: z.string().optional(),
  Hindi: z.string().optional(),
  Tamil: z.string().optional(),
  Kannada: z.string().optional(),
  Marathi: z.string().optional()
});

const AslWebinarRedirectionConfigSchema = z.object({
  match_key: z.string().optional(),
  redirection_config: AslWebinarRedirectionUrlsSchema
});

const SuccessPaymentWithWebinarRedirectionSchema = z.object({
  title: z.string(),
  description: z.string(),
  url: z.string(),
  success_button_text: z.string().optional(),
  show_success_button: z.boolean().optional(),
  webinar_variant: z.string().optional(),
  redirectionConfig: AslWebinarRedirectionConfigSchema
});

const RegistrationMessagesConfigSchema = z.object({
  success_payment: z.union([
    SuccessPaymentSchema,
    SuccessPaymentWithWebinarRedirectionSchema
  ]),
  failure_payment: FailurePaymentSchema,
  button_type: z.string().optional(),
  whatsapp_button_config: WhatsappButtonConfigSchema.optional(),
  success_payment_messages_based_on_field: z
    .array(SuccessPaymentMessageBasedOnFieldSchema)
    .optional()
});

const SegmentEventFieldsSchema = z.object({
  type: z.string(),
  field: z.string(),
  key: z.string().optional(),
  dependent_field: z.string().optional(),
  extra_info_dependent_field: z.string().optional(),
  dependent_form_field: z.string().optional(),
  match_value: z.string().optional(),
  date_format: z.string().optional(),
  time_format: z.string().optional(),
  dependent_segment_field: z.string().optional(),
  preferredDateFormFieldKey: z.string().optional(),
  preferredSlotFormFieldKey: z.string().optional()
});

const SegmentEventSchema = z.object({
  event_name: z.string(),
  event_fields: z.array(SegmentEventFieldsSchema),
  segment_integration_config_list: z.array(z.string()).optional()
});

const GoogleAnalyticsEventsSchema = z.record(
  z.object({
    event_properties: z
      .object({
        event: z.string(),
        frontend_product_path_id: z.boolean().optional(),
        frontend_form_path_id: z.boolean().optional()
      })
      .catchall(z.string())
  })
);

const EventsTypeSchema = z.object({
  should_send_segment_event: z.boolean().optional(),
  segment_events_types: z.record(SegmentEventSchema).optional(),
  google_analytics_events: GoogleAnalyticsEventsSchema.optional()
});

const ProductSegmentConfigSchema = EventsTypeSchema.optional();

export const InstallmentType = z.enum(['EMI', 'CUSTOMIZABLE', 'PERIODIC']);

export const EmiSelectionType = z.enum(['PRE_ORDER', 'POST_ORDER']);

const InstallmentPricingDetailsSchema = z.object({
  installment_type: InstallmentType,
  emi_selection: EmiSelectionType.optional(),
  available_emi_installment_configs: z
    .array(
      z.object({
        no_of_months: z.number(),
        interest_rate: z.number()
      })
    )
    .optional(),
  periodic_installment_pricing_configs: z
    .array(
      z.object({
        config_id: z.string(),
        no_of_installments: z.number(),
        interval_type: z.enum(['MONTHS']),
        interval_value: z.number(),
        interest_amount_in_least_subunit: z.number()
      })
    )
    .optional()
});

export const PricingType = z.enum(['FULL_PAYMENT', 'INSTALLMENT']);

export const DiscountTypeEnum = z.enum([
  'REFERRAL',
  'PAID_CUSTOMER',
  'COUPON',
  'COVID',
  'REFUND_PRODUCT'
]);

export type DiscountTypeEnumType = z.infer<typeof DiscountTypeEnum>;

export const DiscountApplicableOnEnum = z.enum([
  'DOWN_PAYMENT_AMOUNT',
  'INSTALLMENTS_AMOUNT',
  'FULL_PAYMENT_AMOUNT'
]);

export type DiscountApplicableOnEnumType = z.infer<
  typeof DiscountApplicableOnEnum
>;

const PreviousProductDetailsSchema = z.object({
  product_code: z.string(),
  amount_paid_by_user: z.number(),
  is_advance_product: z.boolean(),
  product_display_name: z.string()
});

export type PreviousProductDetailsType = z.infer<
  typeof PreviousProductDetailsSchema
>;

const ProductPurchaseDetailsSchema = z.object({
  applicable_discounts: z
    .array(
      z.object({
        discount_type: DiscountTypeEnum,
        discount_amount: z.number(),
        discount_applicable_on: DiscountApplicableOnEnum,
        paid_customer_products_details: z
          .array(
            z.object({
              product_code: z.string(),
              product_display_name: z.string()
            })
          )
          .optional()
      })
    )
    .optional(),
  change_program_details: z
    .object({
      previous_products_details: z.array(PreviousProductDetailsSchema),
      refund_details: z.object({
        refund_amount: z.number()
      })
    })
    .optional(),
  pricing_details: z.object({
    total_amount_to_be_paid: z.number().optional(),
    total_amount_to_be_paid_in_least_subunit: z.number().optional(),
    emi_installment_details: z
      .object({
        down_payment_amount_to_be_paid: z.number(),
        down_payment_amount_to_be_paid_in_least_subunit: z.number(),
        applied_no_of_months: z.number().optional(),
        interest_rate_amount_to_be_paid: z.number().optional(),
        interest_rate_amount_to_be_paid_in_least_subunit: z.number().optional()
      })
      .optional(),
    customizable_installment_details: z
      .object({
        minimum_down_payment_amount_to_be_paid_in_least_subunit: z.number(),
        total_principal_amount_to_be_paid_in_least_subunit: z.number(),
        minimum_down_payment_amount_to_be_paid: z.number(),
        total_principal_amount_to_be_paid: z.number()
      })
      .optional(),
    periodic_installment_details: z
      .object({
        down_payment_amount_to_be_paid: z.number(),
        down_payment_amount_to_be_paid_in_least_subunit: z.number(),
        applied_pricing_config_id: z.string(),
        principal_amount_to_be_paid: z.number(),
        principal_amount_to_be_paid_in_least_subunit: z.number()
      })
      .optional()
  })
});

export const GetProductDetailsResponseSchema = z.object({
  product_code: z.string(),
  extra_data: z
    .string()
    .refine(validateJSONString(ProductExtraDataSchema, false))
    .optional(),
  is_active: z.boolean(),
  user_extra_info_config: z
    .string()
    .refine(validateJSONString(MultiStepFormFieldsDataSchema, false))
    .optional(),
  product_info: z.string().optional(),
  product_config: z
    .string()
    .refine(validateJSONString(ProductSegmentConfigSchema, false))
    .optional(),
  program: z.string(),
  program_extra_info: z.string(),
  currency: z.string(),
  dependent_product_code: z.string().optional(),
  dependent_product_code_condition: z.enum(['OR', 'AND']),
  allowed_no_of_purchases: z.number(),
  program_info: z.string().optional(),
  program_config: z.string().optional(),
  program_image_url: z.string().optional(),
  program_subscription_url: z.string().optional(),
  error_messages_config: z.string().optional(),
  registration_messages_config: z
    .string()
    .refine(validateJSONString(RegistrationMessagesConfigSchema, false))
    .optional(),
  tnc_to_accept: z.array(z.string()),
  original_amount_in_least_subunit: z.number(),
  original_amount: z.number(),
  product_details: z.object({
    pricing_type: PricingType,
    product_price: z.number(),
    installment_pricing_details: InstallmentPricingDetailsSchema.optional(),
    applicable_discounts: z
      .array(
        z.object({
          discount_type: z.enum(['REFERRAL']),
          referral_discount_details: z.array(
            z.object({
              association: z.string(),
              discount_amount: z.number(),
              discount_amount_in_least_subunit: z.number()
            })
          )
        })
      )
      .optional()
  }),
  product_purchase_details_for_user: ProductPurchaseDetailsSchema
});

export type GetProductDetailsResponseType = z.infer<
  typeof GetProductDetailsResponseSchema
>;

export const PaymentServicesEnum = z.enum(['RAZORPAY', 'SHOP_SE', 'JUSPAY']);

export const PaymentModesEnum = z.enum(['BANK_TRANSFER', 'DIGITAL_PAYMENT']);

export type PaymentServicesEnumType = z.infer<typeof PaymentServicesEnum>;
export type PaymentModesEnumType = z.infer<typeof PaymentModesEnum>;

export const GetPaymentServicesResponseSchema = z.object({
  payment_services_details: z.array(
    z.object({
      payment_service: PaymentServicesEnum,
      payment_service_description: z.string(),
      payment_modes: z.string()
    })
  )
});

export type GetPaymentServicesResponseType = z.infer<
  typeof GetPaymentServicesResponseSchema
>;

const GetReferralCodesResponseSchema = z.object({
  invite_code: z.string().optional(),
  affiliate_code: z.string().optional(),
  pinscale_referral_code: z.string().optional()
});

export type GetReferralCodesResponseType = z.infer<
  typeof GetReferralCodesResponseSchema
>;

export const ReferralCodeStatusEnum = z.enum(['ACTIVE', 'EXPIRED']);

const GetReferralCodeResponseSchema = z.object({
  referral_code: z.string().nullable(),
  status: ReferralCodeStatusEnum.nullable()
});

export type GetReferralCodeResponseType = z.infer<
  typeof GetReferralCodeResponseSchema
>;

export const UserReferralTrackRequestSchema = z.object({
  product_code: z.string(),
  existing_licence_code: z.string(),
  attempted_licence_code: z.string()
});

export type UserReferralTrackRequestType = z.infer<
  typeof UserReferralTrackRequestSchema
>;

export const UserReferralTrackResponseSchema = z.object({
  response: z.string(),
  http_status_code: z.number()
});

export type UserReferralTrackResponseType = z.infer<
  typeof UserReferralTrackResponseSchema
>;

export const GetUserOrderReferralDiscountRequestSchema = z.object({
  product_code: z.string(),
  invite_code: z.string().optional(),
  affiliate_code: z.string().optional(),
  coupon_code: z.string().optional(),
  periodic_installment_details: PeriodicInstallmentDetailsSchema
});

export type GetUserOrderReferralDiscountRequestType = z.infer<
  typeof GetUserOrderReferralDiscountRequestSchema
>;

const GetUserOrderReferralDiscountResponseSchema = z.object({
  amount_in_least_subunit: z.number(),
  amount: z.number(),
  referral_discount_amount_in_least_subunit: z.number(),
  referral_discount_amount: z.number(),
  original_amount_in_least_subunit: z.number(),
  original_amount: z.number(),
  product_details: z.object({
    pricing_type: PricingType,
    product_price: z.number(),
    installment_pricing_details: InstallmentPricingDetailsSchema.optional()
  }),
  product_purchase_details_for_user: ProductPurchaseDetailsSchema
});

export type GetUserOrderReferralDiscountResponseType = z.infer<
  typeof GetUserOrderReferralDiscountResponseSchema
>;

export const GetOrderUpdateUserExtraInfoRequestSchema = z.object({
  order_id: z.string(),
  user_extra_info: z.string().optional(),
  extra_info_based_on_product_config: z.string().optional()
});

export type GetOrderUpdateUserExtraInfoRequestType = z.infer<
  typeof GetOrderUpdateUserExtraInfoRequestSchema
>;

export const GetBackendSegmentEventRequestSchema = z.object({
  event: z.string(),
  event_data: z.string(),
  order_id: z.string(),
  integrations: z.string().optional()
});

export type GetBackendSegmentEventRequestType = z.infer<
  typeof GetBackendSegmentEventRequestSchema
>;

export const GetOrderStatusRequestSchema = z.object({
  order_id: z.string()
});

export type GetOrderStatusRequestType = z.infer<
  typeof GetOrderStatusRequestSchema
>;

const orderStatusType = z.enum([
  'ACTIVE',
  'EXPIRED',
  'REFUNDED',
  'INITIATED',
  'IN_PROGRESS',
  'FAILED',
  'CHANGED_PROGRAM'
]);

export const GetOrderStatusResponseSchema = z.object({
  order_id: z.string(),
  order_status: orderStatusType,
  payments: z
    .array(
      z.object({
        payment_status: z
          .enum([
            'CREATED',
            'ATTEMPTED',
            'CANCELLED',
            'SUCCESS',
            'FAILED',
            'REFUNDED'
          ])
          .optional(),
        payment_id: z.string().optional(),
        order_payment_status: z
          .enum(['ACTIVE', 'IN_ACTIVE', 'IN_PROGRESS'])
          .optional()
      })
    )
    .optional(),
  registration_messages_config: z
    .string()
    .refine(validateJSONString(RegistrationMessagesConfigSchema, false))
    .optional(),
  product_code: z.string()
});

export type GetOrderStatusResponseType = z.infer<
  typeof GetOrderStatusResponseSchema
>;

const BaseCreateOrderRequestSchema = z.object({
  product_code: z.string(),
  no_of_months: z.number().optional(),
  periodic_installment_details: PeriodicInstallmentDetailsSchema,
  invite_code: z.string().optional(),
  coupon_code: z.string().optional(),
  extra_info_based_on_product_config: z.string().optional(),
  accepted_tnc: z.array(z.string()),
  user_order_gst_details: z
    .object({
      gst_registered: z.enum(['YES', 'NO']),
      company_name: z.string().optional(),
      gst_in: z.string().optional(),
      address: z.string().optional(),
      pin_code: z.string().optional(),
      city: z.string().optional(),
      gst_type: z
        .enum(['REGULAR', 'SEZ', 'COMPOSITION', 'DEEMED_EXPORTS'])
        .optional()
    })
    .optional()
});

export const GetCreateFreeOrderRequestSchema =
  BaseCreateOrderRequestSchema.extend({
    user_details: z.object({
      full_name: z.string().optional(),
      email: z.string().email().optional(),
      phone_number: z.string(),
      occupation: z.string().optional(),
      state_of_residence: z.string().optional(),
      gender: z.string().optional(),
      i_want_to_receive_updates_directly_on_whatsapp: z.string().optional(),
      affiliate_code: z.string().optional()
    })
  });

export type GetCreateFreeOrderRequestType = z.infer<
  typeof GetCreateFreeOrderRequestSchema
>;

export const GetCreateFreeOrderResponseSchema = z.object({
  order_id: z.string(),
  order_status: orderStatusType,
  create_free_order_success: z.boolean()
});

export type GetCreateFreeOrderResponseType = z.infer<
  typeof GetCreateFreeOrderResponseSchema
>;

export const GetCreatePaymentOrderResponseSchema = z.object({
  order_id: z.string(),
  pg_order_id: z.string().optional(),
  razorpay_key: z.string().optional(),
  amount_in_least_subunit: z.number(),
  amount: z.number(),
  currency: z.string(),
  company_name: z.string(),
  company_description: z.string(),
  image: z.string(),
  prefill_email: z.string().email(),
  prefill_name: z.string(),
  prefill_contact: z.string(),
  original_amount_in_least_subunit: z.number().optional(),
  original_amount: z.number().optional(),
  referral_discount_amount_in_least_subunit: z.number().optional(),
  referral_discount_amount: z.number().optional(),
  payment_service: PaymentServicesEnum,
  shop_se_order_transaction_details: z
    .object({
      payment_link: z.string().url().optional()
    })
    .optional(),
  juspay_order_transaction_details: z
    .object({
      payment_link: z.string().url()
    })
    .optional(),
  razorpay_smart_collect_details: z
    .object({
      bank_account_number: z.string(),
      ifsc_code: z.string(),
      bank_name: z.string(),
      merchant_name: z.string()
    })
    .optional()
});

export type GetCreatePaymentOrderResponseType = z.infer<
  typeof GetCreatePaymentOrderResponseSchema
>;

const CustomizableInstallmentDetailsSchema = z.object({
  duration_to_pay_remaining_amount: z.object({
    duration_type: z.enum(['DAY', 'MONTH']),
    duration_value: z.number()
  }),
  next_installment_datetime: z.string()
});

export const GetCreatePaymentOrderRequestSchema =
  BaseCreateOrderRequestSchema.extend({
    user_details: z.object({
      full_name: z.string(),
      email: z.string().email(),
      phone_number: z.string(),
      occupation: z.string().optional(),
      state_of_residence: z.string().optional(),
      gender: z.string().optional(),
      i_want_to_receive_updates_directly_on_whatsapp: z.string().optional(),
      affiliate_code: z.string().optional()
    }),
    amount_to_be_paid_in_least_subunit: z.number(),
    payment_service: PaymentServicesEnum,
    payment_mode: PaymentModesEnum,
    customizable_installment_details:
      CustomizableInstallmentDetailsSchema.optional(),
    shopse_details: z
      .object({
        full_name: z.string().optional(),
        email: z.string().optional(),
        gender: z.string().optional(),
        pincode: z.string().optional()
      })
      .optional(),
    post_payment_redirection_url: z.string().url().optional()
  });

export type GetCreatePaymentOrderRequestType = z.infer<
  typeof GetCreatePaymentOrderRequestSchema
>;

const UserOrderDetailsSchema = z.object({
  product_code: z.string(),
  amount_in_least_subunit: z.number(),
  amount: z.number(),
  extra_data: z.string().optional(),
  is_verified_phone_number_required: z.boolean(),
  is_verified_email_required: z.boolean(),
  is_active: z.boolean(),
  user_extra_info_config: z.string().optional(),
  product_info: z.string().optional(),
  product_config: z.string().optional(),
  redirect_url_after_registration: z.string().optional(),
  referral_discount_amount_in_least_subunit: z.number().optional(),
  referral_discount: z.number().optional(),
  paid_customers_discount_in_least_subunit: z.number(),
  paid_customers_discount: z.number(),
  advance_paid: z.number(),
  already_paid_other_than_advance: z.number(),
  advance_paid_in_least_subunit: z.number(),
  already_paid_other_than_advance_in_least_subunit: z.number(),
  coupon_discount: z.number(),
  coupon_discount_in_least_subunit: z.number(),
  refund_product_discount_amount_in_least_subunit: z.number(),
  refund_product_discount_amount: z.number(),
  have_to_accept_tnc: z.string().optional(),
  covid_discount_amount_in_least_subunit: z.number(),
  covid_discount_amount: z.number(),
  user_order_details: z.object({
    order_id: z.string(),
    starts_on_datetime: z.string(),
    expires_on_datetime: z.string().optional(),
    order_extra_data: z.string().optional(),
    extra_info_based_on_product_config: z.string().optional(),
    order_active_at: z.string().nullable().optional(),
    amount_paid_in_least_subunit: z.number().optional(),
    amount_to_be_paid_in_least_subunit: z.number().optional()
  })
});

export type UserOrderDetailsType = z.infer<typeof UserOrderDetailsSchema>;

const UserOrderOtherPlansSchema = z.object({
  product_code: z.string(),
  amount_in_least_subunit: z.number(),
  amount: z.number(),
  extra_data: z.string(),
  is_verified_phone_number_required: z.boolean(),
  is_verified_email_required: z.boolean(),
  is_active: z.boolean(),
  user_extra_info_config: z.string(),
  product_info: z.string(),
  product_config: z.string(),
  redirect_url_after_registration: z.string(),
  referral_discount_amount_in_least_subunit: z.number(),
  referral_discount: z.number(),
  paid_customers_discount_in_least_subunit: z.number(),
  paid_customers_discount: z.number(),
  advance_paid: z.number(),
  already_paid_other_than_advance: z.number(),
  advance_paid_in_least_subunit: z.number(),
  already_paid_other_than_advance_in_least_subunit: z.number(),
  coupon_discount: z.number(),
  coupon_discount_in_least_subunit: z.number(),
  refund_product_discount_amount_in_least_subunit: z.number(),
  refund_product_discount_amount: z.number(),
  covid_discount_amount_in_least_subunit: z.number(),
  covid_discount_amount: z.number()
});

const UserOrdersDetailsSchema = z.object({
  program: z.string(),
  program_extra_info: z.string(),
  program_info: z.string().optional(),
  program_config: z.string().optional(),
  program_image_url: z.string().optional(),
  program_subscription_url: z.string().optional(),
  registered: z.array(UserOrderDetailsSchema),
  expired: z.array(UserOrderDetailsSchema),
  other_plans: z.array(UserOrderOtherPlansSchema)
});

const GetUserOrdersDetailsResponseSchema = z.object({
  user_orders_details: z.array(UserOrdersDetailsSchema)
});

export type GetUserOrdersDetailsResponseType = z.infer<
  typeof GetUserOrdersDetailsResponseSchema
>;

export const InstallmentOrderDetailsFilterValueEnum = z.enum([
  'CREATED',
  'IN_PROGRESS',
  'PAID',
  'CLOSED'
]);

export const InstallmentOrderDetailsFilterKeyEnum = z.enum([
  'INSTALLMENT_ORDER_STATUS'
]);

export const GetInstallmentOrderDetailsRequestSchema = z.object({
  filters: z.array(
    z.object({
      key: InstallmentOrderDetailsFilterKeyEnum,
      value: InstallmentOrderDetailsFilterValueEnum
    })
  )
});

export type GetInstallmentOrderDetailsRequestType = z.infer<
  typeof GetInstallmentOrderDetailsRequestSchema
>;

export const GetInstallmentOrderDetailsStatusEnum = z.enum([
  'CREATED',
  'IN_PROGRESS',
  'PAID',
  'CLOSED'
]);

export const GetInstallmentOrderDetailsInstallmentTypeEnum = z.enum([
  'EMI',
  'CUSTOMIZABLE',
  'PERIODIC'
]);

export const GetInstallmentOrderDetailsStatusTypeEnum = z.enum([
  'PAID',
  'UNPAID',
  'UPCOMING',
  'PARTIALLY_PAID',
  'ONGOING'
]);

const GetInstallmentOrderDetailsSchema = z.object({
  order_id: z.string(),
  installment_details: z.object({
    installment_order_id: z.string(),
    installment_order_status: GetInstallmentOrderDetailsStatusEnum,
    installment_type: GetInstallmentOrderDetailsInstallmentTypeEnum,
    program_code: z.string(),
    product_code: z.string(),
    product_name: z.string(),
    extra_info: z.string(),
    customizable_installment_details: z.object({
      amount_paid: z.number(),
      remaining_amount_to_be_paid: z.number(),
      minimum_amount_to_be_paid_for_installment: z.number(),
      zero_interest_rate_applicable: z.boolean(),
      zero_interest_rate_till_datetime: z.string()
    }),
    periodic_installment_details: z.array(
      z.object({
        id: z.string(),
        total_amount_to_be_paid: z.number(),
        amount_paid: z.number(),
        due_datetime: z.string(),
        paid_datetime: z.string(),
        status: GetInstallmentOrderDetailsStatusTypeEnum
      })
    )
  })
});

export const GetInstallmentOrderDetailsResponseSchema = z.object({
  orders_details: z.array(GetInstallmentOrderDetailsSchema)
});

export type GetInstallmentOrderDetailsResponseType = z.infer<
  typeof GetInstallmentOrderDetailsResponseSchema
>;

export const GetInstallmentOrderDetailsFailureResponseStatusEnum = z.enum([
  'INVALID_FILTER_KEY_EXCEPTION',
  'INVALID_FILTER_VALUE_EXCEPTION'
]);

export const GetInstallmentOrderDetailsFailureResponseSchema = z.object({
  response: z.string(),
  http_status_code: z.number(),
  res_status: GetInstallmentOrderDetailsFailureResponseStatusEnum
});

export type GetInstallmentOrderDetailsFailureResponseType = z.infer<
  typeof GetInstallmentOrderDetailsFailureResponseSchema
>;

export const GetProductsPurchaseDetailsRequestSchema = z.object({
  product_codes: z.array(z.string()),
  invite_code: z.string(),
  affiliate_code: z.string()
});

export type GetProductsPurchaseDetailsRequestType = z.infer<
  typeof GetProductsPurchaseDetailsRequestSchema
>;

export const GetProductsPurchaseDetailsResponseSchema = z.object({
  products_details: z.array(GetProductDetailsResponseSchema)
});

export type GetProductsPurchaseDetailsResponseType = z.infer<
  typeof GetProductsPurchaseDetailsResponseSchema
>;

// Add new schemas for user_orders/details/v1/ endpoint
const FilterSchema = z.object({
  key: z.enum(['ORDER_STATUS']),
  value: z.enum([
    'ACTIVE',
    'EXPIRED',
    'REFUNDED',
    'INITIATED',
    'IN_PROGRESS',
    'FAILED',
    'CHANGED_PROGRAM'
  ])
});

export const GetUserOrdersDetailsRequestSchema = z.object({
  filters: z.array(FilterSchema).optional()
});

export type GetUserOrdersDetailsRequestType = z.infer<
  typeof GetUserOrdersDetailsRequestSchema
>;

const UserOrderDiscountDetailsSchema = z.object({
  discount_type: z.enum([
    'REFERRAL',
    'PAID_CUSTOMER',
    'COUPON',
    'COVID',
    'REFUND_PRODUCT',
    'PAYMENT_PLAN'
  ]),
  discount_amount: z.number()
});

const EMIPeriodSchema = z.object({
  start_date: z.string(),
  end_date: z.string().optional()
});

const PaidInstallmentDetailsSchema = z.object({
  amount_paid: z.number(),
  paid_datetime: z.string().datetime()
});

const NBFCLoanApplicationSchema = z.object({
  application_id: z.string(),
  nbfc_name_enum: z.string(),
  loan_amount: z.number(),
  tenure: z.number(),
  status: z.enum([
    'ACTIVE',
    'PAUSED',
    'CANCELLED',
    'FORECLOSED',
    'COMPLETED',
    'CLOSED'
  ]),
  amount_collected: z.number().optional(),
  disbursed_datetime: z.string().datetime().optional(),
  emi_period: EMIPeriodSchema
});

const MonthlyInstallmentServiceApplicationSchema = z.object({
  application_id: z.string(),
  service_provider: z.enum(['JODO_FLEX']),
  amount: z.number(),
  no_of_installments: z.number(),
  status: z.enum([
    'ACTIVE',
    'PAUSED',
    'CANCELLED',
    'FORECLOSED',
    'COMPLETED',
    'CLOSED'
  ]),
  paid_installments_details: z.array(PaidInstallmentDetailsSchema).optional()
});

const EMIPaymentDetailsSchema = z.object({
  tenure: z.number(),
  nbfc_loan_applications: z.array(NBFCLoanApplicationSchema).optional(),
  monthly_installment_service_applications: z
    .array(MonthlyInstallmentServiceApplicationSchema)
    .optional()
});

const CustomizableInstallmentOrderDetailsSchema = z.object({
  installment_order_id: z.string(),
  installment_order_status: z.enum([
    'CREATED',
    'IN_PROGRESS',
    'PAID',
    'CLOSED'
  ]),
  minimum_amount_to_be_paid_for_installment: z.number(),
  zero_interest_rate_applicable: z.boolean(),
  zero_interest_rate_till_datetime: z.string()
});

const PeriodicInstallmentOrderDetailsSchema = z.object({
  installment_order_id: z.string(),
  installment_order_status: z.enum([
    'CREATED',
    'IN_PROGRESS',
    'PAID',
    'CLOSED'
  ]),
  installment_details: z.array(
    z.object({
      id: z.string(),
      total_amount_to_be_paid: z.number(),
      amount_paid: z.number(),
      due_datetime: z.string(),
      paid_datetime: z.string(),
      status: GetInstallmentOrderDetailsStatusTypeEnum
    })
  )
});

const InstallmentPaymentDetailsSchema = z.object({
  installment_type: z.enum(['EMI', 'CUSTOMIZABLE', 'PERIODIC']),
  emi_payment_details: EMIPaymentDetailsSchema.optional(),
  customizable_installment_order_details:
    CustomizableInstallmentOrderDetailsSchema.optional(),
  periodic_installment_order_details:
    PeriodicInstallmentOrderDetailsSchema.optional()
});

const UserOrderDetailsFilterSchema = z.object({
  order_id: z.string(),
  product_code: z.string(),
  order_status: z.enum([
    'ACTIVE',
    'EXPIRED',
    'REFUNDED',
    'INITIATED',
    'IN_PROGRESS',
    'FAILED',
    'CHANGED_PROGRAM'
  ]),
  order_active_at: z.string().datetime(),
  amount_paid: z.number(),
  product_price: z.number(),
  applied_discounts: z.array(UserOrderDiscountDetailsSchema),
  pricing_type: PricingType,
  installment_payment_details: InstallmentPaymentDetailsSchema.optional()
});

export type UserOrderDetailsFilterType = z.infer<
  typeof UserOrderDetailsFilterSchema
>;

export const GetUserOrdersDetailsWithFiltersResponseSchema = z.object({
  user_orders: z.array(UserOrderDetailsFilterSchema)
});

export type GetUserOrdersDetailsWithFiltersResponseType = z.infer<
  typeof GetUserOrdersDetailsWithFiltersResponseSchema
>;

// Export additional schema types for BaseProductDetail
export type ProductExtraDataType = z.infer<typeof ProductExtraDataSchema>;
export type RegistrationMessagesConfigType = z.infer<
  typeof RegistrationMessagesConfigSchema
>;
export type ProductSegmentConfigType = z.infer<
  typeof ProductSegmentConfigSchema
>;
export type MultiStepFormFieldsDataType = z.infer<
  typeof MultiStepFormFieldsDataSchema
>;
export type ErrorMessageObjectType = z.infer<typeof ErrorMessageObjectSchema>;
export type InstallmentPricingDetailsType = z.infer<
  typeof InstallmentPricingDetailsSchema
>;
export type ProductPurchaseDetailsType = z.infer<
  typeof ProductPurchaseDetailsSchema
>;

// Extract the applicable_discounts type from product_details
const ApplicableDiscountsSchema = z.array(
  z.object({
    discount_type: z.enum(['REFERRAL']),
    referral_discount_details: z.array(
      z.object({
        association: z.string(),
        discount_amount: z.number(),
        discount_amount_in_least_subunit: z.number()
      })
    )
  })
);

export type ApplicableDiscountsType = z.infer<typeof ApplicableDiscountsSchema>;

// Cancel Order schemas
export const CancelOrderRequestSchema = z.object({
  order_id: z.string()
});

export type CancelOrderRequestType = z.infer<typeof CancelOrderRequestSchema>;

export const CancelOrderResponseSchema = z.object({
  response: z.string(),
  http_status_code: z.number()
});

export type CancelOrderResponseType = z.infer<typeof CancelOrderResponseSchema>;

// User Rewards schemas
export const GetUserRewardsRequestSchema = z.object({
  client_id: z.string(),
  user_id: z.string()
});

export type GetUserRewardsRequestType = z.infer<
  typeof GetUserRewardsRequestSchema
>;

export const GetUserRewardsResponseSchema = z.object({
  order_id: z.string(),
  reward: z.number()
});

export type GetUserRewardsResponseType = z.infer<
  typeof GetUserRewardsResponseSchema
>;

// User Referral IDs schemas
export const GetUserReferralIdsRequestSchema = z.object({
  client_id: z.string(),
  user_id: z.string()
});

export type GetUserReferralIdsRequestType = z.infer<
  typeof GetUserReferralIdsRequestSchema
>;

export const GetUserReferralIdsResponseSchema = z.object({
  referral_ids: z.array(z.string())
});

export type GetUserReferralIdsResponseType = z.infer<
  typeof GetUserReferralIdsResponseSchema
>;

// User Subscriptions schemas
export const GetUserSubscriptionsRequestSchema = z.object({
  client_id: z.string(),
  user_id: z.string()
});

export type GetUserSubscriptionsRequestType = z.infer<
  typeof GetUserSubscriptionsRequestSchema
>;

export const GetUserSubscriptionsResponseSchema = z.object({
  subscriptions: z.array(
    z.object({
      subscription_id: z.string(),
      product_code: z.string(),
      status: z.string(),
      created_at: z.string().datetime(),
      updated_at: z.string().datetime()
    })
  )
});

export type GetUserSubscriptionsResponseType = z.infer<
  typeof GetUserSubscriptionsResponseSchema
>;

// Order Full Refund schemas
export const OrderFullRefundRequestSchema = z.object({
  client_id: z.string(),
  order_id: z.string(),
  refund_reason: z.string().optional()
});

export type OrderFullRefundRequestType = z.infer<
  typeof OrderFullRefundRequestSchema
>;

export const OrderFullRefundResponseSchema = z.object({
  response: z.string(),
  http_status_code: z.number(),
  refund_id: z.string().optional()
});

export type OrderFullRefundResponseType = z.infer<
  typeof OrderFullRefundResponseSchema
>;

// Get Latest In Progress Order schemas
export const GetLatestInProgressOrderResponseSchema = z.object({
  order_id: z.string(),
  amount: z.number(),
  currency: z.string(),
  status: z.string(),
  created_at: z.string().datetime()
});

export type GetLatestInProgressOrderResponseType = z.infer<
  typeof GetLatestInProgressOrderResponseSchema
>;

// Order Details schemas
export const GetOrderDetailsRequestSchema = z.object({
  client_id: z.string()
});

export type GetOrderDetailsRequestType = z.infer<
  typeof GetOrderDetailsRequestSchema
>;

export const GetOrderDetailsResponseSchema = z.object({
  order_id: z.string(),
  amount: z.number(),
  currency: z.string(),
  status: z.string(),
  product_code: z.string(),
  user_id: z.string(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime()
});

export type GetOrderDetailsResponseType = z.infer<
  typeof GetOrderDetailsResponseSchema
>;

// User Tags schemas
export const GetUserTagsRequestSchema = z.object({
  user_id: z.string()
});

export type GetUserTagsRequestType = z.infer<typeof GetUserTagsRequestSchema>;

export const GetUserTagsResponseSchema = z.object({
  user_tags: z.array(z.string())
});

export type GetUserTagsResponseType = z.infer<typeof GetUserTagsResponseSchema>;

// User Order EMI Installment Details schemas
export const GetUserOrderEmiInstallmentDetailsRequestSchema = z.object({
  order_id: z.string()
});

export type GetUserOrderEmiInstallmentDetailsRequestType = z.infer<
  typeof GetUserOrderEmiInstallmentDetailsRequestSchema
>;

export const GetUserOrderEmiInstallmentDetailsResponseSchema = z.object({
  installment_details: z.array(
    z.object({
      installment_id: z.string(),
      amount: z.number(),
      due_date: z.string().datetime(),
      status: z.string()
    })
  )
});

export type GetUserOrderEmiInstallmentDetailsResponseType = z.infer<
  typeof GetUserOrderEmiInstallmentDetailsResponseSchema
>;

// User Order EMI Price Details schemas
export const GetUserOrderEmiPriceDetailsRequestSchema = z.object({
  order_id: z.string(),
  tenure: z.number()
});

export type GetUserOrderEmiPriceDetailsRequestType = z.infer<
  typeof GetUserOrderEmiPriceDetailsRequestSchema
>;

export const GetUserOrderEmiPriceDetailsResponseSchema = z.object({
  monthly_amount: z.number(),
  total_amount: z.number(),
  interest_rate: z.number(),
  processing_fee: z.number().optional()
});

export type GetUserOrderEmiPriceDetailsResponseType = z.infer<
  typeof GetUserOrderEmiPriceDetailsResponseSchema
>;

// User Order Billing Details schemas
export const GetUserOrderBillingDetailsRequestSchema = z.object({
  order_id: z.string()
});

export type GetUserOrderBillingDetailsRequestType = z.infer<
  typeof GetUserOrderBillingDetailsRequestSchema
>;

export const GetUserOrderBillingDetailsResponseSchema = z.object({
  billing_address: z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string(),
    address_line_1: z.string(),
    address_line_2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    country: z.string(),
    postal_code: z.string()
  }),
  order_details: z.object({
    order_id: z.string(),
    amount: z.number(),
    currency: z.string(),
    status: z.string()
  })
});

export type GetUserOrderBillingDetailsResponseType = z.infer<
  typeof GetUserOrderBillingDetailsResponseSchema
>;

// User Order Details schemas (single order)
export const GetUserOrderDetailsRequestSchema = z.object({
  order_id: z.string()
});

export type GetUserOrderDetailsRequestType = z.infer<
  typeof GetUserOrderDetailsRequestSchema
>;

export const GetUserOrderDetailsResponseSchema = z.object({
  order_id: z.string(),
  product_code: z.string(),
  order_status: z.enum([
    'ACTIVE',
    'EXPIRED',
    'REFUNDED',
    'INITIATED',
    'IN_PROGRESS',
    'FAILED',
    'CHANGED_PROGRAM'
  ]),
  order_active_at: z.string().datetime(),
  amount_paid: z.number(),
  product_price: z.number(),
  applied_discounts: z.array(UserOrderDiscountDetailsSchema),
  pricing_type: PricingType,
  installment_payment_details: InstallmentPaymentDetailsSchema.optional()
});

export type GetUserOrderDetailsResponseType = z.infer<
  typeof GetUserOrderDetailsResponseSchema
>;

// Change Program schemas - CORRECTED based on actual Swagger definitions
export const CreateChangeProgramOrderRequestSchema = z.object({
  new_product_code: z.string(),
  amount_to_be_paid_in_least_subunit: z.number(),
  accepted_change_program_tnc: z.boolean(),
  invite_code: z.string().optional(),
  affiliate_code: z.string().optional(),
  extra_info_based_on_product_config: z.string().optional(),
  accepted_tnc: z.array(z.string()),
  no_of_months: z.number().optional(),
  coupon_code: z.string().optional(),
  customizable_installment_details: z
    .object({
      duration_to_pay_remaining_amount: z.object({
        duration_type: z.enum(['DAY', 'MONTH']),
        duration_value: z.number()
      }),
      next_installment_datetime: z.string().datetime()
    })
    .optional(),
  periodic_installment_details: z
    .object({
      config_id: z.string()
    })
    .optional(),
  payment_mode: z.enum(['BANK_TRANSFER', 'DIGITAL_PAYMENT']).optional(),
  post_payment_redirection_url: z.string().optional(),
  // GST Details
  user_order_gst_details: z
    .object({
      gst_registered: z.enum(['YES', 'NO']),
      company_name: z.string().optional(),
      gst_in: z.string().optional(),
      address: z.string().optional(),
      pin_code: z.string().optional(),
      city: z.string().optional(),
      gst_type: z
        .enum(['REGULAR', 'SEZ', 'COMPOSITION', 'DEEMED_EXPORTS'])
        .optional()
    })
    .optional()
});

export type CreateChangeProgramOrderRequestType = z.infer<
  typeof CreateChangeProgramOrderRequestSchema
>;

export const CreateChangeProgramOrderResponseSchema = z.object({
  order_id: z.string(),
  amount: z.number(),
  currency: z.string(),
  key: z.string(),
  order_creation_time: z.string().datetime()
});

export type CreateChangeProgramOrderResponseType = z.infer<
  typeof CreateChangeProgramOrderResponseSchema
>;

// Change Program Referral Discount - CORRECTED based on actual Swagger
export const GetChangeProgramReferralDiscountRequestSchema = z.object({
  order_id: z.string(),
  invite_code: z.string().optional(),
  affiliate_code: z.string().optional(),
  coupon_code: z.string().optional()
});

export type GetChangeProgramReferralDiscountRequestType = z.infer<
  typeof GetChangeProgramReferralDiscountRequestSchema
>;

export const UpdateChangeProgramOrderRequestSchema = z.object({
  amount_to_be_paid_in_least_subunit: z.number(),
  order_id: z.string(),
  customizable_installment_details: z
    .object({
      duration_to_pay_remaining_amount: z.object({
        duration_type: z.enum(['DAY', 'MONTH']),
        duration_value: z.number()
      }),
      next_installment_datetime: z.string().datetime()
    })
    .optional(),
  periodic_installment_details: z
    .object({
      config_id: z.string()
    })
    .optional(),
  shopse_details: z
    .object({
      full_name: z.string().optional(),
      email: z.string().optional(),
      gender: z.enum(['MALE', 'FEMALE', 'OTHER']).optional(),
      pincode: z.string().optional()
    })
    .optional(),
  payment_mode: z.enum(['BANK_TRANSFER', 'DIGITAL_PAYMENT']).optional(),
  payment_service: z.enum(['SHOP_SE', 'RAZORPAY', 'JUSPAY']).optional(),
  post_payment_redirection_url: z.string().optional(),
  // GST Details
  user_order_gst_details: z
    .object({
      gst_registered: z.enum(['YES', 'NO']),
      company_name: z.string().optional(),
      gst_in: z.string().optional(),
      address: z.string().optional(),
      pin_code: z.string().optional(),
      city: z.string().optional(),
      gst_type: z
        .enum(['REGULAR', 'SEZ', 'COMPOSITION', 'DEEMED_EXPORTS'])
        .optional()
    })
    .optional()
});

export type UpdateChangeProgramOrderRequestType = z.infer<
  typeof UpdateChangeProgramOrderRequestSchema
>;

export const UpdateChangeProgramOrderResponseSchema = z.object({
  response: z.string(),
  http_status_code: z.number(),
  order_status: z.string()
});

export type UpdateChangeProgramOrderResponseType = z.infer<
  typeof UpdateChangeProgramOrderResponseSchema
>;

// Change Program Product Details - CORRECTED based on actual Swagger
export const GetChangeProgramProductDetailsRequestSchema = z.object({
  order_id: z.string()
});

export type GetChangeProgramProductDetailsRequestType = z.infer<
  typeof GetChangeProgramProductDetailsRequestSchema
>;

// Using GetProductDetailsResponseType for change program product details
// export type GetChangeProgramProductDetailsResponseType = GetProductDetailsResponseType; // Removed - use GetProductDetailsResponseType directly

// User Product Count schemas - CORRECTED based on actual Swagger
export const GetUserWiseProductCountRequestSchema = z.object({
  client_id: z.string(),
  client_secret: z.string(),
  user_ids: z.array(z.string()),
  referral_code_type: z.enum(['AFFILIATE_CODE', 'INVITE_CODE']),
  only_given_product_codes: z.array(z.string()).optional(),
  start_datetime: z.string().datetime().optional(),
  end_datetime: z.string().datetime().optional()
});

export type GetUserWiseProductCountRequestType = z.infer<
  typeof GetUserWiseProductCountRequestSchema
>;

export const GetUserWiseProductCountResponseSchema = z.object({
  user_product_counts: z.array(
    z.object({
      user_id: z.string(),
      product_count: z.number()
    })
  )
});

export type GetUserWiseProductCountResponseType = z.infer<
  typeof GetUserWiseProductCountResponseSchema
>;

// Users Rewards schemas - CORRECTED based on actual Swagger
export const GetUsersRewardsRequestSchema = z.object({
  client_id: z.string(),
  client_secret: z.string(),
  user_ids: z.array(z.string()),
  referral_code_type: z.enum(['AFFILIATE_CODE', 'INVITE_CODE']),
  association: z.string()
});

export type GetUsersRewardsRequestType = z.infer<
  typeof GetUsersRewardsRequestSchema
>;

export const GetUsersRewardsResponseSchema = z.object({
  users_rewards: z.array(
    z.object({
      user_id: z.string(),
      total_rewards: z.number(),
      rewards_details: z.array(
        z.object({
          reward_id: z.string(),
          reward_amount: z.number(),
          reward_status: z.string(),
          created_at: z.string().datetime()
        })
      )
    })
  )
});

export type GetUsersRewardsResponseType = z.infer<
  typeof GetUsersRewardsResponseSchema
>;

// Affiliate Customers Rewards schemas - CORRECTED based on actual Swagger
export const GetAffiliateCustomersRewardsRequestSchema = z.object({
  client_id: z.string(),
  client_secret: z.string(),
  affiliate_user_id: z.string(),
  referral_code_type: z.enum(['AFFILIATE_CODE', 'INVITE_CODE']),
  product_codes: z.array(z.string()).optional(),
  is_included_given_products_only: z.boolean().optional()
});

export type GetAffiliateCustomersRewardsRequestType = z.infer<
  typeof GetAffiliateCustomersRewardsRequestSchema
>;

export const GetAffiliateCustomersRewardsResponseSchema = z.object({
  customers_rewards: z.array(
    z.object({
      customer_user_id: z.string(),
      product_code: z.string(),
      reward_amount: z.number(),
      order_date: z.string().datetime()
    })
  )
});

export type GetAffiliateCustomersRewardsResponseType = z.infer<
  typeof GetAffiliateCustomersRewardsResponseSchema
>;

// User Reward Create schemas
export const CreateUserRewardRequestSchema = z.object({
  client_id: z.string(),
  user_id: z.string(),
  reward_amount: z.number(),
  reward_type: z.string(),
  order_id: z.string().optional()
});

export type CreateUserRewardRequestType = z.infer<
  typeof CreateUserRewardRequestSchema
>;

export const CreateUserRewardResponseSchema = z.object({
  reward_id: z.string(),
  status: z.string()
});

export type CreateUserRewardResponseType = z.infer<
  typeof CreateUserRewardResponseSchema
>;

// Order Bulk schemas
export const GetOrderDetailsBulkRequestSchema = z.object({
  client_id: z.string(),
  order_ids: z.array(z.string())
});

export type GetOrderDetailsBulkRequestType = z.infer<
  typeof GetOrderDetailsBulkRequestSchema
>;

export const GetOrderDetailsBulkResponseSchema = z.object({
  orders: z.array(GetOrderDetailsResponseSchema)
});

export type GetOrderDetailsBulkResponseType = z.infer<
  typeof GetOrderDetailsBulkResponseSchema
>;

// Get User Orders with Statuses schemas
export const GetUserOrdersWithStatusesRequestSchema = z.object({
  client_id: z.string(),
  user_id: z.string(),
  order_statuses: z.array(z.string())
});

export type GetUserOrdersWithStatusesRequestType = z.infer<
  typeof GetUserOrdersWithStatusesRequestSchema
>;

export const GetUserOrdersWithStatusesResponseSchema = z.object({
  orders: z.array(GetUserOrderDetailsResponseSchema)
});

export type GetUserOrdersWithStatusesResponseType = z.infer<
  typeof GetUserOrdersWithStatusesResponseSchema
>;

// Create Negative Rewards and Bonuses schemas
export const CreateNegativeRewardsAndBonusesRequestSchema = z.object({
  client_id: z.string(),
  order_id: z.string(),
  negative_amount: z.number(),
  reason: z.string()
});

export type CreateNegativeRewardsAndBonusesRequestType = z.infer<
  typeof CreateNegativeRewardsAndBonusesRequestSchema
>;

export const CreateNegativeRewardsAndBonusesResponseSchema = z.object({
  response: z.string(),
  http_status_code: z.number()
});

export type CreateNegativeRewardsAndBonusesResponseType = z.infer<
  typeof CreateNegativeRewardsAndBonusesResponseSchema
>;

// Update Rewards Statuses schemas
export const UpdateRewardsStatusesRequestSchema = z.object({
  client_id: z.string(),
  reward_updates: z.array(
    z.object({
      reward_id: z.string(),
      new_status: z.string()
    })
  )
});

export type UpdateRewardsStatusesRequestType = z.infer<
  typeof UpdateRewardsStatusesRequestSchema
>;

export const UpdateRewardsStatusesResponseSchema = z.object({
  response: z.string(),
  http_status_code: z.number(),
  updated_count: z.number()
});

export type UpdateRewardsStatusesResponseType = z.infer<
  typeof UpdateRewardsStatusesResponseSchema
>;

// Get Positive Rewards for Negative Rewards schemas
export const GetPositiveRewardsForNegativeRewardsRequestSchema = z.object({
  client_id: z.string(),
  negative_reward_ids: z.array(z.string())
});

export type GetPositiveRewardsForNegativeRewardsRequestType = z.infer<
  typeof GetPositiveRewardsForNegativeRewardsRequestSchema
>;

export const GetPositiveRewardsForNegativeRewardsResponseSchema = z.object({
  positive_rewards: z.array(
    z.object({
      negative_reward_id: z.string(),
      positive_reward_id: z.string(),
      positive_reward_amount: z.number()
    })
  )
});

export type GetPositiveRewardsForNegativeRewardsResponseType = z.infer<
  typeof GetPositiveRewardsForNegativeRewardsResponseSchema
>;

// Users Registered Products schemas
export const GetUsersRegisteredProductsRequestSchema = z.object({
  client_id: z.string(),
  user_ids: z.array(z.string())
});

export type GetUsersRegisteredProductsRequestType = z.infer<
  typeof GetUsersRegisteredProductsRequestSchema
>;

export const GetUsersRegisteredProductsResponseSchema = z.object({
  users_products: z.array(
    z.object({
      user_id: z.string(),
      registered_products: z.array(
        z.object({
          product_code: z.string(),
          product_name: z.string(),
          registration_date: z.string().datetime(),
          status: z.string()
        })
      )
    })
  )
});

export type GetUsersRegisteredProductsResponseType = z.infer<
  typeof GetUsersRegisteredProductsResponseSchema
>;

// Update User Product Usage Details schemas
export const UpdateUserProductUsageDetailsRequestSchema = z.object({
  client_id: z.string(),
  user_id: z.string(),
  product_code: z.string(),
  usage_details: z.object({
    last_login: z.string().datetime().optional(),
    total_login_count: z.number().optional(),
    features_used: z.array(z.string()).optional()
  })
});

export type UpdateUserProductUsageDetailsRequestType = z.infer<
  typeof UpdateUserProductUsageDetailsRequestSchema
>;

export const UpdateUserProductUsageDetailsResponseSchema = z.object({
  response: z.string(),
  http_status_code: z.number()
});

export type UpdateUserProductUsageDetailsResponseType = z.infer<
  typeof UpdateUserProductUsageDetailsResponseSchema
>;
